/**
 * LSP-15388 — returning the user to the project after a mobile "Add Content" action starts.
 *
 * NOTE ON SCOPE: this module is the shared implementation for the SIBLING flows — ActionPhoto,
 * ActionStartRecording and ActionVideo. ActionUploadPhoto deliberately keeps its own private copy;
 * see the block comment above its `currentPathname()` for why, and do not "tidy" that duplication
 * away without reading it first.
 *
 * On mobile, ProjectActionFAB.navigateToAction does NOT open the Action* component in a dialog over
 * the project the way desktop does — it routes to a STANDALONE page
 * (`/photo`, `/async-recording`, `/request-recording`, `/live-call`, `/upload`) carrying
 * `?projectId=…&from=project`. That unmounts EntityDetailPage, which owns every project-side
 * progress affordance and the EventBus listeners the capture/upload flows publish to.
 *
 * Each Action* component signalled the project flow with `$emit("close-menu", …)` and then
 * deliberately skipped the router push. On desktop that is correct — the dialog sits over a mounted
 * project page and the parent FAB handles the close. On mobile there is no parent listening and no
 * project page mounted, so the user was left stranded on a standalone form with nothing to show for
 * the action they just started.
 *
 * (EntityDetailPage is the exclusive listener for "FileUploaded"; EntityActions also registers
 * UploadStarted/UploadFinished, but it renders inside EntityDetailPage's header and unmounts with
 * it, so both are gone once the standalone route takes over.)
 *
 * This module is the shared decision + navigation used by the sibling Action* flows. It is
 * deliberately dependency-free (takes `route`/`router` rather than reading a component instance) so
 * it can be unit-tested directly and reused from Options-API components.
 */

import { logInfo } from "@/helpers/logError";

type RouteLike =
	| {
			name?: unknown;
			// Read by isOnProjectPage. Omitting it made that branch unreachable for any typed caller
			// (and a TS2339 under strict TS) — it only shipped because neither vite build nor esbuild
			// typechecks. Review finding.
			path?: unknown;
			query?: Record<string, unknown>;
	  }
	| null
	| undefined;

type RouterLike =
	| {
			push: (target: unknown) => unknown;
	  }
	| null
	| undefined;

/**
 * The PATH only, anchored — never a whole-href `.includes("/projects")` test, which misreads a
 * conversation page carrying `?returnTo=/projects/x` and an unrelated `/projects-archive` route.
 * A constant base is used because an opaque document reports a "null" origin (and some hosts omit
 * it entirely), which made `new URL(href, window.location.origin)` throw for an ordinary relative
 * href.
 */
export function currentPathname(componentName: string): string {
	try {
		return new URL(window.location.href, "http://relative.invalid").pathname;
	} catch (error) {
		console.warn(`${componentName}: could not parse the current URL, assuming not a project page`, {
			tags: { method: "currentPathname", file: "projectFlowReturn" },
			extra: { href: window.location.href, error }
		});
		return "";
	}
}

const PROJECT_PATH = /^\/projects(\/|$)/i;

/**
 * The router is authoritative about which component is mounted, so consult it first — `name`, then
 * its own normalized `path`. `window.location` is only the last resort, for callers that have
 * neither. (Reading the raw href in preference to `route.path` was a regression: the two agree in
 * the real app but the router's view is the correct source, and the existing ActionVideo suite
 * rightly pins that.)
 */
export function isOnProjectPage(route: RouteLike, componentName: string): boolean {
	const routeName = route?.name;
	if (routeName === "project" || routeName === "project-shared") {
		return true;
	}
	const routePath = route?.path;
	if (typeof routePath === "string" && routePath.length > 0) {
		return PROJECT_PATH.test(routePath);
	}
	return PROJECT_PATH.test(currentPathname(componentName));
}

export function isProjectFlow(route: RouteLike, componentName: string): boolean {
	if (isOnProjectPage(route, componentName)) {
		return true;
	}
	// vue-router hands a repeated query key over as an array.
	const fromQuery = route?.query?.from;
	return (Array.isArray(fromQuery) ? fromQuery[0] : fromQuery) === "project";
}

/**
 * A navigation problem must never escape into the caller: by the time this runs the user's action
 * (capture, recording, upload) has ALREADY started, so throwing here would report a failure for
 * something that is working. `$router.push` can reject AND can throw synchronously; an aborted,
 * cancelled or duplicated navigation does neither — it RESOLVES with a NavigationFailure, which is
 * an Error subclass. Both handlers are attached directly to the push result so the original
 * rejection is still consumed exactly once.
 */
export function navigateAfterActionStarted(options: {
	router: RouterLike;
	target: unknown;
	componentName: string;
	context?: Record<string, unknown>;
	/**
	 * How to name the destination in this call's diagnostics, e.g. "to the conversation". Cursor
	 * (LOW): the non-project conversation redirects were routed through here for their navigation
	 * hardening, and inherited navigateSafely's project-return wording and method tag with it — so a
	 * failed conversation hand-off logged as a project return that never ran. Defaulted rather than
	 * required so the hardening is never skipped for want of a label.
	 */
	destination?: string;
}): void {
	navigateSafely(options.router, options.target, options.componentName, options.context ?? {}, {
		destination: options.destination ?? "to the requested route",
		method: "navigateAfterActionStarted"
	});
}

function navigateSafely(
	router: RouterLike,
	target: unknown,
	componentName: string,
	context: Record<string, unknown>,
	labels: { destination: string; method: string }
): boolean {
	const warnNavigationFailed = (error: unknown) => {
		console.warn(`${componentName}: navigation ${labels.destination} failed`, {
			tags: { method: labels.method, file: "projectFlowReturn" },
			extra: { ...context, error }
		});
	};
	// ls-heroku-ci (HIGH): RouterLike permits null/undefined, and `Promise.resolve(router?.push(...))`
	// resolves with `undefined` in that case — no throw, no rejection, so not one warn branch fires,
	// and the caller still reported "a navigation was issued". Every other failure path in this file
	// logs; this one silently claimed success. A defensive-typing gap rather than an observed incident,
	// but the whole point of this helper is that a no-op says why.
	if (!router) {
		console.warn(`${componentName}: no router available, cannot navigate ${labels.destination}`, {
			tags: { method: labels.method, file: "projectFlowReturn" },
			extra: { ...context }
		});
		return false;
	}
	try {
		const navigation = Promise.resolve(router?.push(target));
		navigation.catch(warnNavigationFailed);
		navigation.then(
			(outcome) => {
				if (outcome instanceof Error) {
					warnNavigationFailed(outcome);
				}
			},
			() => undefined
		);
	} catch (error) {
		warnNavigationFailed(error);
		// A synchronous throw means the push never happened, so this is the same "no navigation was
		// issued" answer the missing-router path gives. Returning true here would make the value lie
		// to the first caller that decides to branch on it.
		return false;
	}
	return true;
}

/**
 * Put the user back on the project page now that their action is under way.
 *
 * Returns true when a navigation was issued. No-ops (and says why) when the project page is already
 * mounted beneath a dialog, or when no usable project id is available — pushing a blank or
 * non-string id navigates to a project that cannot exist, which is a worse dead end than staying put.
 */
export function returnToProjectAfterActionStarted(options: {
	route: RouteLike;
	router: RouterLike;
	projectId: unknown;
	componentName: string;
}): boolean {
	const { route, router, projectId: rawProjectId, componentName } = options;
	if (isOnProjectPage(route, componentName)) {
		// ls-heroku-ci (HIGH): this was the one exit here with no breadcrumb, while its sibling below
		// warns and ActionUploadPhoto's private copy logs the equivalent branch. It is a correct no-op —
		// the project is already mounted under the dialog, so close-menu is the whole contract — but
		// "correct" and "silent" are different things, and this repo's diagnostic rule exists because a
		// silent early return in exactly this family cost hours three times. logInfo, not warn: nothing
		// is wrong here.
		logInfo(`${componentName}: already on the project page, close-menu owns the hand-off`, {
			tags: { method: "returnToProjectAfterActionStarted", file: "projectFlowReturn" },
			extra: { rawProjectId, routeQuery: route?.query, href: window.location.href }
		});
		return false;
	}
	const projectId = typeof rawProjectId === "string" ? rawProjectId.trim() : "";
	if (!projectId) {
		console.warn(`${componentName}: from=project but no usable projectId, cannot return to the project`, {
			tags: { method: "returnToProjectAfterActionStarted", file: "projectFlowReturn" },
			extra: { rawProjectId, routeQuery: route?.query, href: window.location.href }
		});
		return false;
	}
	return navigateSafely(
		router,
		{ name: "project", params: { id: projectId } },
		componentName,
		{ projectId },
		{
			destination: "back to the project",
			method: "returnToProjectAfterActionStarted"
		}
	);
}
